/* Copyright (c) 2020-present CloudIO Inc */

import {
  checkForFixedChild,
  toOptions,
} from 'componentregistry/utils/ManagerUtils';
import { getManager } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Drawer';
import { helpRenders } from './Drawer_Help';

const name = 'Drawer';

const DrawerManager: ComponentManagerFn<'Drawer'> = (
  platform: Platform,
): ComponentManager<'Drawer'> => {
  return {
    ...baseComponentManager<'Drawer'>(platform, 'Drawer'),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    isChildAllowed: (page, item, childType, context) =>
      (
        [
          'CanvasGrid',
          'Card',
          'Chart',
          'DataGrid',
          'DataStoreReference',
          'FlexPanel',
          'Form',
          'HorizontalSplitPanel',
          'OCRComponent',
          'RowPanel',
          'SearchForm',
          'TabPanel',
          'VerticalSplitPanel',
        ] as ItemType[]
      ).includes(childType) &&
      checkForFixedChild(platform, page, item, childType, name, context),
    props: {
      anchor: {
        label: 'Anchor',
        type: 'select',
        options: toOptions(['bottom', 'left', 'right', 'top']),
        required: true,
      },
      modal: {
        label: 'Modal',
        type: 'boolean',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      drawerSize: {
        label: 'Size',
        type: 'numberInput',
        required: true,
      },
    },
    category: 'Containers',
    name,
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isItemIdUsed(itm, item.itemId);
      });
      return usage.length
        ? `${item.props.anchor} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.anchor;
    },
    canHaveChildren: () => true,
    layout: 'fixed',
    icon: { iconName: 'window-maximize' },
    showEmptyPlaceholderInDesignerView: false,
    description: `**Overview**<br/>
The Drawer is a navigational container that slides in from the edge of the screen to present supplementary content. Unlike a modal dialog, which sits in the center of the screen, the Drawer stays anchored to the side, making it ideal for displaying details, forms, or filters without completely obscuring the main page context.
Use the Drawer to keep users focused on their current task while accessing secondary information—such as editing a selected record or adjusting search parameters—without navigating away.

**1. Positioning & Sizing**<br/>
The Drawer is flexible and can be adapted to fit various layout requirements depending on where the content needs to appear.
* **Anchor:** Determines which edge of the screen the drawer slides in from. (Left / Right are common for navigation or details; Top / Bottom are useful for notifications or settings).
* **Size:** Defines the physical dimension of the drawer. (Sets Width if anchored Left/Right, or Height if anchored Top/Bottom).

**2. Interaction & Behavior**<br/>
You can control how the Drawer interacts with the rest of the application and how users perceive it.
* **Modal:** Toggles the background interaction mode. (Enabled/True creates a dark overlay blocking the main page; Disabled/False keeps the main page interactive, useful for reference panels).
* **Open:** Controls the visibility state of the drawer. This property is typically bound to a variable or logic trigger (e.g., a button click) to programmatically open or close the panel.

**3. Content Composition**<br/>
The Drawer acts as a robust container capable of hosting complex UI elements. It is designed to handle "fixed layout" children, ensuring that components like grids or charts render correctly within the sliding panel.
* **Supported Content:** You can populate the drawer with layout containers (like Flex Panel or Split Panel) or functional components (like Forms, Data Grids, or Charts).
* **Layout Safety:** The component includes internal validation to prevent invalid layout combinations, ensuring that content sizes predictably regardless of the drawer's state.`,
    helpRenders,
  };
};

export default DrawerManager;
